import { useState, useEffect } from 'react';
import { Responsive, WidthProvider, Layout as GridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import styles from '../../organisms/widget/widget.module.scss';
import gridStyles from './grid-layout.module.scss';
import { useSetAtom } from 'jotai';
import clsx from 'clsx';
import { dashboardType } from 'stores/dashboard-store/dashboard-store';
import { getClientLayoutForBreakpoint } from './layouts/layoutUtils/layoutUtils';
import {
  containerBreakpoints,
  useBreakpoint
} from '../../../../../providers/breakpoint/BreakpointProvider';
import { TableProps } from 'modules/dashboard/hooks/TableRenderer';
import { ChartProps } from 'modules/dashboard/utils/chart/ChartWidget';
import Widget, { WidgetProps } from '../widget/Widget';
import { WidgetRenderer } from 'modules/dashboard/hooks/WidgetRenderer';
import { useWidgetActions, WidgetActions } from 'modules/dashboard/utils/widget/widgetActions';

interface GridLayoutProps {
  pageType: string;
}

export type ExtendedGridLayout = GridLayout & {
  widgetProps: WidgetProps | null;
  chartProps: ChartProps | null;
  tableProps?: TableProps | null;
};

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const GridLayoutShell = ({ pageType }: GridLayoutProps) => {
  const breakpointContext = useBreakpoint();

  const [layout, setLayout] = useState<ExtendedGridLayout[]>([]);
  const widgetActions: WidgetActions = useWidgetActions();
  const setDashboardType = useSetAtom(dashboardType);

  const hotLinksData = [
    { path: '/proposals', label: 'New business', icon: 'action_note-add' },
    { path: '/other-apps', label: 'Other apps', icon: 'navigation_apps' },
    { path: '/legal', label: 'Legal', icon: 'action-2_secured-file' },
    { path: '/support', label: 'Support', icon: 'action-2_contact-support' },
    { path: '/other-apps', label: 'Other apps', icon: 'navigation_apps' },
    { path: '/legal', label: 'Legal', icon: 'action-2_secured-file' },
    { path: '/support', label: 'Support', icon: 'action-2_contact-support' },
    { path: '/other-apps', label: 'Other apps', icon: 'navigation_apps' }
  ];

  const [isInitialized, setIsInitialized] = useState(false);

  // Handle initial setup of grid layout to avoid race condition
  useEffect(() => {
    if (!isInitialized) {
      if (pageType === 'intermediary') {
        setDashboardType('intermediary');
        const newLayout = getClientLayoutForBreakpoint(breakpointContext.breakpoint);
        setLayout(newLayout);
      } else {
        setDashboardType('client');
        setLayout(getClientLayoutForBreakpoint(breakpointContext.breakpoint, true));
      }
      setIsInitialized(true);
    }
  }, [pageType, breakpointContext.breakpoint, isInitialized, setDashboardType]);

  // Handle breakpoint changes after initial setup of grid layout
  useEffect(() => {
    if (isInitialized) {
      const newLayout = getClientLayoutForBreakpoint(
        breakpointContext.breakpoint,
        pageType === 'client'
      );
      setLayout(newLayout);
    }
  }, [breakpointContext.breakpoint, isInitialized, pageType]);

  const onLayoutChange = (newLayout: ExtendedGridLayout[]) => {
    const updatedLayout = newLayout.map((item) => {
      const existingWidget = layout.find((w) => w.i === item.i);
      return {
        ...item,
        widgetProps: existingWidget?.widgetProps || null,
        chartProps: existingWidget?.chartProps || null,
        tableProps: existingWidget?.tableProps || null
      };
    });

    setLayout(updatedLayout);
    localStorage.setItem('dashboardLayout', JSON.stringify(updatedLayout));
  };

  return (
    <div className={gridStyles.container}>
      <ResponsiveReactGridLayout
        className='layout'
        breakpoints={containerBreakpoints} // Note: matched to actual grid usablewidth (not width of screen)
        cols={{ lg: 12, md: 9, sm: 7 }}
        layouts={{ [breakpointContext.breakpoint]: layout }}
        onBreakpointChange={(newBp: string) => {
          breakpointContext.setBreakpoint(newBp as 'lg' | 'md' | 'sm');
        }}
        rowHeight={80}
        onLayoutChange={onLayoutChange}
        isDraggable={false}
        isResizable={false}
        resizeHandles={[]}
        preventCollision={true}
        verticalCompact={true}
        compactType={null}
        containerPadding={[0, 16]}
        margin={{ lg: [24, 24], md: [16, 16], sm: [16, 16] }}
      >
        {layout.map((item, index) => {
          const widgetConfig = item?.widgetProps?.widgetConfig;
          return (
            <Widget
              isEditing={false}
              className={clsx(styles.widget, false && styles.edit)}
              key={item.i}
              style={{ animationDelay: `${index * 100}ms` }}
              widgetConfig={{
                heading: item.i,
                key: item.i,
                helpHeadingText: widgetConfig?.helpHeadingText,
                helpSubText: widgetConfig?.helpSubText,
                type: widgetConfig?.type,
                removeElements: widgetConfig?.removeElements,
                isEditButton: widgetConfig?.isEditButton,
                actionButton: null,
                customActionIcon: widgetConfig?.customActionIcon,
                customActionText: widgetConfig?.customActionText,
                isHidable: widgetConfig?.isHidable,
                hasProperties: widgetConfig?.hasProperties,
                hideMode: widgetConfig?.hideMode,
                hideMoreButton: widgetConfig?.hideMoreButton,
                isScrollable: widgetConfig?.isScrollable,
                onInfoClick: widgetActions[widgetConfig?.type as keyof typeof widgetActions]
              }}
            >
              <WidgetRenderer widget={item} hotLinksData={hotLinksData} />
            </Widget>
          );
        })}
      </ResponsiveReactGridLayout>
    </div>
  );
};

export default GridLayoutShell;
